import * as React from "react";
import { BaseContext, Routes as BaseRoutes } from "universal-router";
import { Dispatch } from "redux";
import PropTypes from "prop-types";

import { RootState } from "../state";

export interface RouteInfo<Params> {
  params: Params;
  path: string;
  query: { [key: string]: any };
  children: RouteInfo<Params>;
  component: React.ComponentType<any>;
}

export type RouteActionResult<Params> = RouteInfo<Params>;

export interface RouteExtraContext {
  waitUntilLoaded: boolean;
}

export type RouteContext<Params> = RouteExtraContext &
  BaseContext<RouteExtraContext, RouteActionResult<Params>, Params>;

export type RouteAction<Params> = (
  context: RouteContext<Params>,
  params: Params
) => RouteActionResult<Params> | Promise<RouteActionResult<Params>>;

export type Routes = BaseRoutes<RouteExtraContext, RouteActionResult<any>>;

export function asyncRouteAction<Comp extends React.ComponentClass<any>>(
  options: AsyncRouteOptions<Comp>
): RouteAction<any> {
  const Component = generateAsyncRouteComponent(options);
  return async (context: RouteContext<any>, params: any) => {
    if (context.waitUntilLoaded) {
      await Component.load();
    }
    const children = await context.next();
    return {
      component: Component,
      path: context.path,
      query: context.query,
      params,
      children,
    };
  };
}

export interface AsyncRouteOptions<Comp extends React.ComponentClass<any>> {
  Placeholder?: React.ComponentType<any>;
  loader(): Promise<{ default: Comp }>;
  isInitialized?(getState: () => RootState, component: Comp): boolean;
  fetchInitialData?(getState: () => RootState, dispatch: Dispatch<RootState>, component: Comp): Promise<any>;
}

export function generateAsyncRouteComponent<Comp extends React.ComponentClass<any>>({
  Placeholder,
  loader,
  isInitialized,
  fetchInitialData,
}: AsyncRouteOptions<Comp>) {
  interface State {
    Component: Comp | undefined;
    isInitialized: boolean;
  }

  let Component: Comp | undefined;

  return class AsyncRouteComponent extends React.Component<any, State> {
    public static contextTypes = {
      store: PropTypes.object.isRequired,
    };

    public static load() {
      return loader()
        .then(m => m.default)
        .then((ResolvedComponent: Comp) => {
          Component = ResolvedComponent;
        });
    }

    constructor(props: any, context: any) {
      super(props);
      this.state = {
        Component,
        isInitialized: isInitialized ? !!Component && isInitialized(context.store.getState, Component) : true,
      };
    }

    public componentDidMount() {
      AsyncRouteComponent.load()
        .then(() => {
          if (isInitialized && !isInitialized(this.context.store.getState, Component!)) {
            return fetchInitialData!(this.context.store.getState, this.context.store.dispatch, Component!);
          }
          return null;
        })
        .then(() => {
          if (this.state.Component !== Component) {
            this.setState({
              Component,
              isInitialized: true,
            });
          }
        });
    }

    public render() {
      if (this.state.Component && this.state.isInitialized) {
        return <this.state.Component {...this.props} />;
      }
      if (Placeholder) {
        return <Placeholder />;
      }
      return null;
    }
  };
}



// WEBPACK FOOTER //
// ./frontend/src/lib/routing.tsx